import { useEffect, useSyncExternalStore } from "react";
import { AiSpreadsheetOperation } from "@/payroll/ai-script/types";

interface PersistedOperation {
  operationId: string;
}

export interface WorkbookOperationSnapshot {
  operation: AiSpreadsheetOperation | null;
  isPending: boolean;
  errorMessage: string | null;
  retry: (() => void) | null;
}

export interface WorkbookOperationRunResult {
  operation: AiSpreadsheetOperation;
  adoptedConflict: boolean;
}

const INITIAL_SNAPSHOT: WorkbookOperationSnapshot = {
  operation: null,
  isPending: false,
  errorMessage: null,
  retry: null,
};

const snapshots = new Map<string, WorkbookOperationSnapshot>();

const listeners = new Map<string, Set<() => void>>();

export const controllers = new Map<string, AbortController>();

const consumers = new Map<string, number>();

export const scopeKey = (businessId: string, runId: string) =>
  `${businessId}:${runId}`;

const storageKey = (businessId: string, runId: string) =>
  `ai-spreadsheet-operation:${scopeKey(businessId, runId)}`;

export const getSnapshot = (key: string) =>
  snapshots.get(key) ?? INITIAL_SNAPSHOT;

export const readWorkbookOperationSnapshot = (
  businessId: string,
  runId: string,
) => getSnapshot(scopeKey(businessId, runId));

const emit = (key: string) =>
  listeners.get(key)?.forEach((listener) => listener());

export const setSnapshot = (
  key: string,
  snapshot: WorkbookOperationSnapshot,
) => {
  snapshots.set(key, snapshot);
  emit(key);
};

export const persistOperation = (
  businessId: string,
  runId: string,
  operation: AiSpreadsheetOperation,
) => {
  if (typeof localStorage === "undefined") return;
  const persisted: PersistedOperation = {
    operationId: operation.id,
  };
  try {
    localStorage.setItem(
      storageKey(businessId, runId),
      JSON.stringify(persisted),
    );
  } catch {
    // Storage is recovery-only and must not break a live mutation.
  }
};

export const clearPersistedOperation = (businessId: string, runId: string) => {
  if (typeof localStorage === "undefined") return;
  try {
    localStorage.removeItem(storageKey(businessId, runId));
  } catch {
    // Storage may be unavailable in a restricted browser.
  }
};

export const readPersistedWorkbookOperation = (
  businessId: string,
  runId: string,
): PersistedOperation | null => {
  if (typeof localStorage === "undefined") return null;

  try {
    const value = localStorage.getItem(storageKey(businessId, runId));
    if (!value) return null;

    const parsed = JSON.parse(value) as Partial<PersistedOperation>;
    if (typeof parsed.operationId === "string")
      return parsed as PersistedOperation;

    clearPersistedOperation(businessId, runId);
    return null;
  } catch {
    clearPersistedOperation(businessId, runId);
    return null;
  }
};

export const completeWorkbookOperation = (
  businessId: string,
  runId: string,
) => {
  clearPersistedOperation(businessId, runId);
  const key = scopeKey(businessId, runId);
  snapshots.delete(key);
  emit(key);
};

export const cancelWorkbookOperationPolling = (
  businessId: string,
  runId: string,
) => {
  const key = scopeKey(businessId, runId);
  controllers.get(key)?.abort();
  controllers.delete(key);
};

export const setWorkbookOperationRetry = (
  businessId: string,
  runId: string,
  retry: (() => void) | null,
) => {
  const key = scopeKey(businessId, runId);
  setSnapshot(key, { ...getSnapshot(key), retry });
};

export const setWorkbookOperationRecoveryError = (
  businessId: string,
  runId: string,
  errorMessage: string,
  retry: () => void,
) => {
  const key = scopeKey(businessId, runId);
  setSnapshot(key, {
    ...getSnapshot(key),
    isPending: false,
    errorMessage,
    retry,
  });
};

export const useWorkbookOperationSnapshot = (
  businessId: string | null,
  runId: string | null,
) => {
  const key = businessId && runId ? scopeKey(businessId, runId) : "";
  const snapshot = useSyncExternalStore(
    (listener) => {
      if (!key) return () => undefined;
      const scopedListeners = listeners.get(key) ?? new Set();
      scopedListeners.add(listener);
      listeners.set(key, scopedListeners);
      return () => {
        scopedListeners.delete(listener);
        if (scopedListeners.size === 0) listeners.delete(key);
      };
    },
    () => (key ? getSnapshot(key) : INITIAL_SNAPSHOT),
    () => INITIAL_SNAPSHOT,
  );

  useEffect(() => {
    if (!key) return;
    consumers.set(key, (consumers.get(key) ?? 0) + 1);
    return () => {
      const remaining = Math.max((consumers.get(key) ?? 1) - 1, 0);
      if (remaining === 0) {
        consumers.delete(key);
        if (businessId && runId) {
          cancelWorkbookOperationPolling(businessId, runId);
        }
      } else {
        consumers.set(key, remaining);
      }
    };
  }, [key]);

  return snapshot;
};
